// src/pages/NotificationSettings.jsx
// Admin-only: push notification status/test + the editable birthday email
// message. Gated both by the sidebar (only System Admin/President ever see
// the nav link) and by the backend (notification_admin.php /
// birthday_email_settings.php both require an admin-role session).
import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { FaBell, FaBirthdayCake, FaPaperPlane, FaUndo, FaSave } from "react-icons/fa";
import { authFetch, getCurrentUser } from "../utils/api";
import PushRecipientRolesPanel from "../components/PushRecipientRolesPanel";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

export default function NotificationSettings() {
  const user = getCurrentUser() || { name: "Unknown User", role: "Member", image: "/user.png" };

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [messageBody, setMessageBody] = useState("");
  const [defaultMessage, setDefaultMessage] = useState("");
  const [loadingMessage, setLoadingMessage] = useState(true);
  const [savingMessage, setSavingMessage] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  const loadStats = () => {
    setLoadingStats(true);
    authFetch(`${ABSOLUTE_HOST}/Backend/notification_admin.php`)
      .then((r) => r.json())
      .then((d) => { if (d?.success) setStats(d); })
      .catch(() => {})
      .finally(() => setLoadingStats(false));
  };

  useEffect(() => {
    loadStats();
    authFetch(`${ABSOLUTE_HOST}/Backend/birthday_email_settings.php`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) {
          setMessageBody(d.message_body || "");
          setDefaultMessage(d.message_body || "");
          setUpdatedAt(d.updated_at);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMessage(false));
  }, []);

  const sendTest = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/notification_admin.php`, {
        method: "POST",
        body: JSON.stringify({ action: "send_test" }),
      });
      const data = await res.json();
      setTestResult({ ok: !!data.success, message: data.message || (data.success ? "Sent." : "Failed.") });
    } catch (e) {
      setTestResult({ ok: false, message: "Network error while sending test notification." });
    } finally {
      setSendingTest(false);
    }
  };

  const saveMessage = async () => {
    if (!messageBody.trim()) return;
    setSavingMessage(true);
    setSaveResult(null);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/birthday_email_settings.php`, {
        method: "POST",
        body: JSON.stringify({ message_body: messageBody.trim(), updated_by_sliit_id: user?.SLIITID || "" }),
      });
      const data = await res.json();
      setSaveResult({ ok: !!data.success, message: data.message || (data.success ? "Saved." : "Failed to save.") });
      if (data.success) setUpdatedAt(new Date().toISOString());
    } catch (e) {
      setSaveResult({ ok: false, message: "Network error while saving." });
    } finally {
      setSavingMessage(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div className="page-head">
            <div>
              <h1>Notification Settings</h1>
              <p className="page-desc">Push notification status and the editable birthday email message. Admins only.</p>
            </div>
          </div>

          {/* Push notifications */}
          <div className="panel" style={{ padding: "var(--sp-4)", marginBottom: "var(--sp-6)" }}>
            <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-4)" }}>
              <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaBell size={14} style={{ color: "var(--accent)" }} /> Mobile Push Notifications
              </h2>
            </div>

            {loadingStats ? (
              <div className="empty-state"><span className="spinner lg" /></div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--sp-4)", marginBottom: "var(--sp-4)" }}>
                <div className="panel tile">
                  <span className="tile-label">Total Subscribed Devices</span>
                  <span className="tile-value num">{stats?.total_subscriptions ?? "—"}</span>
                </div>
                <div className="panel tile">
                  <span className="tile-label">Your Subscribed Devices</span>
                  <span className="tile-value num">{stats?.my_subscriptions ?? "—"}</span>
                </div>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button onClick={sendTest} disabled={sendingTest} className="btn btn-primary btn-sm">
                {sendingTest ? <span className="spinner" /> : <FaPaperPlane size={11} />}
                {sendingTest ? "Sending…" : "Send test notification to me"}
              </button>
              <button onClick={loadStats} className="btn btn-ghost btn-sm">
                <FaUndo size={11} /> Refresh stats
              </button>
            </div>
            {testResult && (
              <div className={testResult.ok ? "success-banner" : "error-banner"} style={{ marginTop: "var(--sp-3)" }}>
                {testResult.message}
              </div>
            )}
            <p className="field-hint" style={{ marginTop: "var(--sp-3)" }}>
              Sends only to your own account's subscribed device(s) -- never broadcasts to other members.
            </p>
          </div>

          {/* Birthday email */}
          <div className="panel" style={{ padding: "var(--sp-4)" }}>
            <div className="section-head" style={{ padding: 0, marginBottom: "var(--sp-4)" }}>
              <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaBirthdayCake size={14} style={{ color: "var(--accent)" }} /> Birthday Email Message
              </h2>
            </div>

            {loadingMessage ? (
              <div className="empty-state"><span className="spinner lg" /></div>
            ) : (
              <>
                <div className="field">
                  <label className="field-label" htmlFor="birthday-message">
                    Message shown in the birthday wish email (subject, layout, and signature stay fixed)
                  </label>
                  <textarea
                    id="birthday-message"
                    rows={5}
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    maxLength={4000}
                  />
                  <p className="field-hint">{messageBody.length}/4000 characters{updatedAt ? ` · Last updated ${new Date(updatedAt).toLocaleString()}` : ""}</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <button onClick={saveMessage} disabled={savingMessage || !messageBody.trim()} className="btn btn-primary btn-sm">
                    {savingMessage ? <span className="spinner" /> : <FaSave size={11} />}
                    {savingMessage ? "Saving…" : "Save message"}
                  </button>
                  <button
                    onClick={() => setMessageBody(defaultMessage)}
                    disabled={savingMessage}
                    className="btn btn-ghost btn-sm"
                  >
                    Reset to current saved value
                  </button>
                </div>
                {saveResult && (
                  <div className={saveResult.ok ? "success-banner" : "error-banner"} style={{ marginTop: "var(--sp-3)" }}>
                    {saveResult.message}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Per-role notification routing */}
          <div style={{ marginTop: "var(--sp-6)" }}>
            <PushRecipientRolesPanel />
          </div>
        </div>
      </main>
    </div>
  );
}
